import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/authContext";
import { Activity, Lock, Mail, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

function fmtErr(detail) {
  if (!detail) return "Erreur inconnue";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success("Connexion réussie", { duration: 1500 });
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail) || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070708] flex items-center justify-center px-4" data-testid="login-page">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #CCFF00 0%, transparent 50%)" }} />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-11 h-11 bg-[#CCFF00] flex items-center justify-center rounded-sm glow-volt">
            <Activity className="w-6 h-6 text-black" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-display text-xl font-bold tracking-tight text-white leading-none">REACH/OS</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">Marketing Réseaux</div>
          </div>
        </div>

        <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-3">// CONNEXION</div>
          <h1 className="font-display text-3xl font-bold text-white mb-1">Bon retour</h1>
          <p className="text-zinc-400 text-sm mb-7">Connecte-toi pour accéder à ton OS marketing.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                <input data-testid="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/50 outline-none transition" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                <input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-10 py-2.5 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/50 outline-none transition" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-zinc-400" data-testid="login-remember-label">
              <input data-testid="login-remember" type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                className="w-4 h-4 accent-[#CCFF00] cursor-pointer" />
              Se souvenir de moi (90 jours)
            </label>

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-[#CCFF00] text-black font-medium py-3 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2 mt-2 glow-volt transition">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-7 pt-5 border-t border-[#1E1E24] text-center">
            <span className="text-xs text-zinc-500">Pas encore de compte ? </span>
            <Link to="/register" data-testid="goto-register" className="text-xs text-[#CCFF00] hover:underline font-medium">Créer un compte</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
